/**
 * Combobox — the searchable-dropdown primitive (Kai: "every dropdown is a
 * searchable dropdown like this" — Freshservice's requester/agent/group
 * pickers). Type-to-filter over a fixed option list, keyboard-navigable,
 * same visual language as every other primitive (border-input, focus-visible
 * ring, rounded-md). Reserve the plain <Select> for genuinely short fixed
 * lists (priority, urgency/impact — 3-4 options need no search); anything
 * whose options come from data (agents, groups, requesters, statuses once
 * they're admin-configurable) uses this instead.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { NO_AUTOFILL } from './input';

export interface ComboboxOption {
  value: string;
  label: string;
  sublabel?: string;
}

export function Combobox({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  emptyText = 'No matches.',
  clearable = true,
  disabled,
  className,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  options: ComboboxOption[];
  placeholder?: string;
  emptyText?: string;
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  // URL state on purpose: this is the combobox's own typeahead buffer. It is
  // cleared when the popover closes and belongs to the control, not the page.
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q) || o.sublabel?.toLowerCase().includes(q));
  }, [options, query]);

  /** Pointer OR focus leaving closes it — Step 208, same as TokenSelect.
   *  A click is not the only way to stop using a control: Tab does it, and so
   *  does a rich-text editor taking focus for itself. `relatedTarget` of null
   *  means focus left the window, which is not a reason to close a list
   *  somebody is about to come back to. */
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onFocusOut = (e: FocusEvent) => {
      const to = e.relatedTarget as Node | null;
      if (!to) return;
      if (rootRef.current && !rootRef.current.contains(to)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  function commit(v: string | null) {
    onChange(v);
    setOpen(false);
  }

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate text-left', !selected && 'text-muted-foreground')}>
          {selected ? selected.label : placeholder}
        </span>
        {clearable && selected && !disabled && (
          <X
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              commit(null);
            }}
          />
        )}
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
          <input
            {...NO_AUTOFILL}
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIndex((i) => Math.max(i - 1, 0));
              } else if (e.key === 'Enter' && filtered[activeIndex]) {
                e.preventDefault();
                commit(filtered[activeIndex]!.value);
              } else if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
            placeholder="Type to filter…"
            className="w-full border-b bg-transparent px-3 py-2 text-sm focus:outline-none"
          />
          <div className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 && <p className="px-3 py-2 text-xs text-muted-foreground">{emptyText}</p>}
            {filtered.map((o, i) => (
              <button
                key={o.value}
                type="button"
                onClick={() => commit(o.value)}
                onMouseEnter={() => setActiveIndex(i)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm',
                  i === activeIndex ? 'bg-accent' : 'hover:bg-accent/60',
                )}
              >
                {o.value === value && <Check className="h-3.5 w-3.5 shrink-0 text-brand" />}
                <span className={cn('min-w-0 flex-1 truncate', o.value !== value && 'pl-[1.375rem]')}>
                  {o.label}
                </span>
                {o.sublabel && <span className="shrink-0 text-xs text-muted-foreground">{o.sublabel}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
